import type { CSSProperties, FC, PropsWithChildren, ReactElement, ReactNode, TransitionEvent } from 'react';
import { useRef } from 'react';

import { DrawerDragHandle } from '../../../components/game/drawer/drawerDragHandle/DrawerDragHandle';
import { useDrawerDrag } from '../../../components/game/drawer/useDrawerDrag';
import { useMaxKeyboardAwareDrawerHeight } from '../../../components/game/drawer/useMaxKeyboardAwareDrawerHeight';
import { useModifiers } from '../../../styles/hooks/useModifiers';
import { BackButton } from '../../controls/backButton/BackButton';
import { IconButton, IconButtonSize, Icons } from '../../controls/iconButton/IconButton';

import type { ClickCallback } from './types';

import styles from './navDrawer.module.css';

export interface NavDrawerProps extends PropsWithChildren {
  open: boolean;
  handleClose: ClickCallback<HTMLElement>;
  handleExpand: (expanded: boolean) => void;
  isExpanded: boolean;
  isForcedExpanded: boolean;
  title: ReactNode;
  handleBack?: ClickCallback<HTMLButtonElement>;
  backButtonVisible?: boolean;
  onTransitionEnd?: (event: TransitionEvent<HTMLDivElement>) => void;
}

export const NavDrawer: FC<NavDrawerProps> = ({
  children,
  handleBack,
  handleClose,
  handleExpand,
  open,
  backButtonVisible,
  title,
  isExpanded,
  isForcedExpanded,
  onTransitionEnd,
}): ReactElement => {
  const drawerRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const maxKeyboardAwareHeight = useMaxKeyboardAwareDrawerHeight();

  const { delta, dragRef, isDragging } = useDrawerDrag({
    onClose: handleClose,
    setExpanded: handleExpand,
    isExpanded,
    open,
  });

  const mods = useModifiers(
    styles,
    'navDrawer',
    {
      expanded: isExpanded,
      forcedExpanded: isForcedExpanded,
      isDragging,
      open,
    },
    { withBaseClass: true }
  );
  const wrapperMods = useModifiers(styles, 'navDrawer__wrapper', { open }, { withBaseClass: true });
  const curtainMods = useModifiers(styles, 'navDrawer__curtain', { open }, { withBaseClass: true });

  return (
    <>
      <div className={curtainMods} onClick={handleClose} />
      <div
        className={wrapperMods}
        ref={wrapperRef}
        style={
          { '--wrapperMaxHeight': maxKeyboardAwareHeight ? `${maxKeyboardAwareHeight}px` : undefined } as CSSProperties
        }
        onTransitionEnd={(e): void => {
          if (e.target === wrapperRef.current) onTransitionEnd?.(e);
        }}
      >
        <div
          data-testid="nav-drawer"
          className={mods}
          style={{ '--delta': `${delta ?? 0}px` } as CSSProperties}
          ref={drawerRef}
        >
          <div data-testid="nav-drawer-header" className={styles.navDrawer__header}>
            {isForcedExpanded ? null : <DrawerDragHandle ref={dragRef} className={`${styles.navDrawer__handle} ${styles.navDrawer__header__item}`} />}
            <BackButton onClick={handleBack} visible={backButtonVisible} className={styles.navDrawer__header__item}/>
            <div data-testid="nav-drawer-header-title" className={`${styles.navDrawer__headerTitle} ${styles.navDrawer__header__item}`}>
              {title}
            </div>
            <span className={styles.navDrawer__header__item}>
              <IconButton
                data-testid="nav-drawer-close-button"
                ghost
                icon={Icons.Close}
                onClick={handleClose}
                size={IconButtonSize.TINY}
              />
            </span>
          </div>
          <div data-testid="nav-drawer-container" className={styles.navDrawer__container}>
            {children}
          </div>
          <div data-testid="nav-drawer-footer" className={styles.navDrawer__footer} />
        </div>
      </div>
    </>
  );
};
